import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FileText } from "lucide-react";
import Label from "../../../../../shared/ui/form/Label";
import Input from "../../../../../shared/ui/form/input/InputField";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";
import { TherapeuticPlan } from "../../../domain/TherapeuticPlan";

interface TherapeuticPlanViewBasicFormProps {
  therapeuticPlan: Partial<TherapeuticPlan>;
  onChange: (updated: Partial<TherapeuticPlan>) => void;
}

export default function TherapeuticPlanViewBasicForm({
  therapeuticPlan,
  onChange,
}: TherapeuticPlanViewBasicFormProps) {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<{ value: string; label: string }[]>([]);

  const currentId = therapeuticPlan.patient_id || therapeuticPlan.couple_patient_id || "";
  const currentLabel = therapeuticPlan.patient_name
    ? `${therapeuticPlan.patient_name} ${therapeuticPlan.patient_last_name || ""}`.trim()
    : t("therapeuticPlan.selectPatient");

  useEffect(() => {
    setPatients([{ value: currentId, label: currentLabel }]);
  }, [currentId, currentLabel]);

  return (
    <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
          <FileText className="w-5 h-5" />
        </div>
        <h3 className="text-xl font-bold text-content-primary">
          {t("therapeuticPlan.basicInfo")}
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Patient */}
        <div>
          <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
            {t("therapeuticPlan.filterPatient")}
          </Label>
          <FilterSelect
            options={patients}
            disabled={true}
            value={currentId}
            placeholder={currentLabel}
            searchPlaceholder={t("patient.searchPlaceholder")}
            onChange={() => { }} // Disabled so onChange is not needed
            className="w-full rounded-xl bg-gray-50/50 border-gray-200/50 focus:bg-white transition-all py-1"
          />
        </div>

        {/* Title */}
        <div>
          <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
            {t("therapeuticPlan.title")}
          </Label>
          <Input
            value={therapeuticPlan.title || ""}
            required
            className="rounded-xl bg-gray-50/50 border-gray-200/50 focus:bg-white transition-all py-3"
            onChange={(e) =>
              onChange({ ...therapeuticPlan, title: e.target.value })
            }
          />
        </div>
      </div>

    </div>
  );
}
